/**
 * ApiKeyManager Container Component
 *
 * Main container for Multi-Key API key management within a pool.
 * Manages key listing, adding, editing, disabling/enabling, and usage viewing.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { clsx } from 'clsx';
import { ApiKeyList } from './ApiKeyList';
import { ApiKeyEditor } from './ApiKeyEditor';
import { ApiKeyUsageChart } from './ApiKeyUsageChart';
import { StatusBadge } from '../shared/StatusBadge';
import {
  llmAdminApi,
  type ApiKeyStatusItem,
  type ApiKeyUsageItem,
} from '../../../services/llmAdminApi';

export interface ApiKeyManagerProps {
  providerName: string;
  poolIndex: number;
  token?: string;
  disabled?: boolean;
  className?: string;
  onRefresh?: () => void;
}

export const ApiKeyManager: React.FC<ApiKeyManagerProps> = ({
  providerName,
  poolIndex,
  token,
  disabled = false,
  className = '',
  onRefresh,
}) => {
  const [keys, setKeys] = useState<ApiKeyStatusItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editorMode, setEditorMode] = useState<'add' | 'edit' | null>(null);
  const [editKeyIndex, setEditKeyIndex] = useState<number | null>(null);
  const [usageData, setUsageData] = useState<ApiKeyUsageItem[] | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [operationLoading, setOperationLoading] = useState(false);

  const fetchKeys = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await llmAdminApi.listPoolKeys(token, providerName, poolIndex);
      setKeys(result.keys);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载 Key 列表失败');
    } finally {
      setLoading(false);
    }
  }, [token, providerName, poolIndex]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const handleAddKey = useCallback(
    async (key: string) => {
      setOperationLoading(true);
      try {
        await llmAdminApi.addPoolKey(token, providerName, poolIndex, key);
        setEditorMode(null);
        await fetchKeys();
        onRefresh?.();
      } catch (err) {
        setError(err instanceof Error ? err.message : '添加 Key 失败');
      } finally {
        setOperationLoading(false);
      }
    },
    [token, providerName, poolIndex, fetchKeys, onRefresh]
  );

  const handleUpdateKey = useCallback(
    async (newKey: string) => {
      if (editKeyIndex === null) return;
      setOperationLoading(true);
      try {
        await llmAdminApi.updatePoolKey(token, providerName, poolIndex, editKeyIndex, newKey);
        setEditorMode(null);
        setEditKeyIndex(null);
        await fetchKeys();
        onRefresh?.();
      } catch (err) {
        setError(err instanceof Error ? err.message : '更新 Key 失败');
      } finally {
        setOperationLoading(false);
      }
    },
    [token, providerName, poolIndex, editKeyIndex, fetchKeys, onRefresh]
  );

  const handleDeleteKey = useCallback(
    async (keyIndex: number) => {
      if (!confirm('确定要删除此 API Key 吗？此操作不可撤销。')) return;
      setOperationLoading(true);
      try {
        await llmAdminApi.deletePoolKey(token, providerName, poolIndex, keyIndex);
        await fetchKeys();
        onRefresh?.();
      } catch (err) {
        setError(err instanceof Error ? err.message : '删除 Key 失败');
      } finally {
        setOperationLoading(false);
      }
    },
    [token, providerName, poolIndex, fetchKeys, onRefresh]
  );

  const handleDisableKey = useCallback(
    async (keyIndex: number) => {
      setOperationLoading(true);
      try {
        await llmAdminApi.disablePoolKey(token, providerName, poolIndex, keyIndex);
        await fetchKeys();
        onRefresh?.();
      } catch (err) {
        setError(err instanceof Error ? err.message : '禁用 Key 失败');
      } finally {
        setOperationLoading(false);
      }
    },
    [token, providerName, poolIndex, fetchKeys, onRefresh]
  );

  const handleEnableKey = useCallback(
    async (keyIndex: number) => {
      setOperationLoading(true);
      try {
        await llmAdminApi.enablePoolKey(token, providerName, poolIndex, keyIndex);
        await fetchKeys();
        onRefresh?.();
      } catch (err) {
        setError(err instanceof Error ? err.message : '启用 Key 失败');
      } finally {
        setOperationLoading(false);
      }
    },
    [token, providerName, poolIndex, fetchKeys, onRefresh]
  );

  const handleViewUsage = useCallback(
    async (keyIndex: number) => {
      setUsageLoading(true);
      setUsageData(null);
      try {
        const result = await llmAdminApi.getPoolKeyUsage(token, providerName, poolIndex, keyIndex);
        setUsageData(result.keys);
      } catch (err) {
        setError(err instanceof Error ? err.message : '获取使用数据失败');
      } finally {
        setUsageLoading(false);
      }
    },
    [token, providerName, poolIndex]
  );

  const enabledCount = keys.filter((k) => k.status === 'enabled').length;

  if (loading) {
    return (
      <div className={clsx('p-6 text-center text-gray-500', className)}>
        <div className="animate-spin w-6 h-6 border-3 border-blue-500 border-t-transparent rounded-full mx-auto mb-3" />
        加载 Key 列表...
      </div>
    );
  }

  return (
    <div className={clsx('space-y-4', className)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h4 className="text-sm font-medium text-gray-700">API Key 管理</h4>
          <StatusBadge
            variant={enabledCount > 0 ? 'success' : 'error'}
            size="sm"
            dot
          >
            {enabledCount}/{keys.length} 可用
          </StatusBadge>
        </div>
        {!disabled && !editorMode && (
          <button
            type="button"
            onClick={() => setEditorMode('add')}
            disabled={operationLoading}
            className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 disabled:opacity-50"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            添加 Key
          </button>
        )}
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="text-red-500 hover:text-red-700"
          >
            关闭
          </button>
        </div>
      )}

      {editorMode === 'add' && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <ApiKeyEditor
            mode="add"
            onSubmit={handleAddKey}
            onCancel={() => setEditorMode(null)}
            disabled={operationLoading}
          />
        </div>
      )}

      {editorMode === 'edit' && editKeyIndex !== null && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <ApiKeyEditor
            mode="edit"
            onSubmit={handleUpdateKey}
            onCancel={() => {
              setEditorMode(null);
              setEditKeyIndex(null);
            }}
            disabled={operationLoading}
          />
        </div>
      )}

      <ApiKeyList
        keys={keys}
        onDisable={handleDisableKey}
        onEnable={handleEnableKey}
        onDelete={handleDeleteKey}
        onViewUsage={handleViewUsage}
        disabled={disabled || operationLoading}
      />

      {usageLoading && (
        <div className="text-center py-3 text-gray-500 text-sm">
          <div className="animate-spin w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2" />
          加载使用数据...
        </div>
      )}

      {usageData && !usageLoading && (
        <div className="p-4 bg-white border border-gray-200 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <h5 className="text-sm font-medium text-gray-700">Key 使用情况</h5>
            <button
              type="button"
              onClick={() => setUsageData(null)}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              关闭
            </button>
          </div>
          <ApiKeyUsageChart keys={usageData} />
        </div>
      )}

      <div className="text-xs text-gray-500">
        提示：每个池至少保留一个 Key。禁用的 Key 不会参与请求路由。
      </div>
    </div>
  );
};

export default ApiKeyManager;
